import { Box, FormLabel, Grid, GridItem } from '@chakra-ui/react'
import { ReactElement } from 'react'
import { PermissionsEnum } from '../../../../../utils'
import { CmFieldTypeEnum } from '../../../../constants'
import PermissionGuard from '../../../PermissionGuard'
import CMBooleanField from './CMBooleanField'
import CMDateField from './CMDateField'
import CMMultiSelectField from './CMMultiSelectField'
import CMNumberField from './CMNumberField'
import CMSingleSelectField from './CMSingleSelectField'
import CMTextareaField from './CMTextareaField'
import CMTextField from './CMTextField'
import type { FormikProps, FormikValues } from 'formik'
import type { CmFieldResponseObject } from '../../../../interfaces'
import { BulkEditFieldObject } from './BulkEditModeDropdown'
import { CmAllowedValue } from '../../../../interfaces/CustomMetadataResponse'

const CMFormField = ({
  cmField,
  disabled,
  form,
}: {
  cmField: CmFieldResponseObject
  disabled: boolean
  form: FormikProps<FormikValues>
}): React.ReactElement => {
  switch (cmField.schema.type) {
    case CmFieldTypeEnum.Boolean:
      return (
        <CMBooleanField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
          form={form}
        />
      )
    case CmFieldTypeEnum.Number:
      return (
        <CMNumberField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
          form={form}
        />
      )
    case CmFieldTypeEnum.Text:
      return <CMTextField cmField={cmField} disabled={disabled} form={form} />
    case CmFieldTypeEnum.Textarea:
      return (
        <CMTextareaField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
          form={form}
        />
      )
    case CmFieldTypeEnum.SingleSelect:
      return (
        <CMSingleSelectField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
          form={form}
        />
      )
    case CmFieldTypeEnum.MultiSelect:
      return (
        <CMMultiSelectField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
          form={form}
        />
      )
    case CmFieldTypeEnum.Date:
      return (
        <CMDateField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
          form={form}
        />
      )
    default:
      throw Error('Should not happen')
  }
}

const CMUploadForm = ({
  form,
  cmFields,
}: {
  form: FormikProps<FormikValues>
  cmFields: CmFieldResponseObject[]
}): ReactElement => (
  <Box position="relative" width="100%" spacing="1.5em" alignItems="flex-start">
    {cmFields.length ? (
      <PermissionGuard
        permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
      >
        {(hasMlManagePermission: boolean) => (
          <>
            {' '}
            <FormLabel htmlFor="name" fontWeight={700}>
              Custom metadata
            </FormLabel>
            <Box display="flex" flexWrap="wrap" gap="20px" marginTop={'20px'}>
              <Grid templateColumns="repeat(11, 1fr)" gap={4} width="100%">
                {cmFields.map((cmField, index) =>
                  index % 2 === 0 ? (
                    <GridItem colSpan={5} h="fit-content" key={cmField.id}>
                      <CMFormField
                        key={cmField.id}
                        cmField={cmField}
                        disabled={!hasMlManagePermission}
                        form={form}
                      />
                    </GridItem>
                  ) : (
                    <GridItem
                      colStart={7}
                      colEnd={12}
                      h="fit-content"
                      key={cmField.id}
                    >
                      <CMFormField
                        key={cmField.id}
                        cmField={cmField}
                        disabled={!hasMlManagePermission}
                        form={form}
                      />
                    </GridItem>
                  ),
                )}
              </Grid>
            </Box>
          </>
        )}
      </PermissionGuard>
    ) : (
      <></>
    )}
  </Box>
)

export default CMUploadForm

export interface CMFieldProps {
  cmField: CmFieldResponseObject
  disabled?: boolean
  readOnly?: boolean
  isUpload?: boolean
  form: FormikProps<FormikValues>
  labelElement?: ReactElement
  showBulkEditFieldModeDropdown?: boolean
  bulkEditFieldMode?: BulkEditFieldObject
  style?: React.CSSProperties
  required?: boolean
  menuPosition?: 'absolute' | 'fixed'
  setBulkEditFieldMode?: React.Dispatch<
    React.SetStateAction<BulkEditFieldObject>
  >
  onInputChange?(cmField: CmFieldResponseObject, form: FormikProps<any>, newValue?: CmAllowedValue): void
  placeholder?: string
}
